import { Field } from '#app/components/form/field.tsx'
import { SelectField } from '#app/components/form/select-field.tsx'
import { type AcknowledgementEntry } from '#app/routes/mocra+/_form+/adverse-events/form.ts'
import { ADVERSE_EVENTS_SECTION_RESOURCES } from '#app/routes/mocra+/_form+/adverse-events/section-resources.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import {
	ACKNOWLEDGEMENT_CHANNEL_OPTIONS,
	ACKNOWLEDGEMENT_WINDOW_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

type Props = {
	readonly value: AcknowledgementEntry
	readonly defaultWindow: AcknowledgementEntry['window']
	readonly onWindowChange: (value: AcknowledgementEntry['window']) => void
	readonly onChannelChange: (value: AcknowledgementEntry['channel']) => void
	readonly onCoversWeekendsChange: (value: boolean) => void
	readonly onCustomWindowChange: (value: string) => void
	readonly windowErrors: readonly string[]
	readonly channelErrors: readonly string[]
	readonly customWindowErrors: readonly string[]
}

export function AcknowledgementSection({
	value,
	defaultWindow,
	onWindowChange,
	onChannelChange,
	onCoversWeekendsChange,
	onCustomWindowChange,
	windowErrors,
	channelErrors,
	customWindowErrors,
}: Props) {
	const acknowledgementWindow = value.window ?? defaultWindow
	const coversWeekends = Boolean(value.coversWeekends)

	return (
		<Section
			title="Acknowledgement target"
			description="Share the service-level target for acknowledging new complaints and how the team responds."
			mocraSections={ADVERSE_EVENTS_SECTION_RESOURCES.mocraSections}
			templates={ADVERSE_EVENTS_SECTION_RESOURCES.templates}
			childrenClassName="space-y-4"
		>
			<SelectField
				labelProps={{
					children: 'How fast do you commit to acknowledge new complaints?',
				}}
				selectProps={{
					name: 'acknowledgementWindow',
					value: acknowledgementWindow,
					onChange: (event) =>
						onWindowChange(event.target.value ?? defaultWindow),
				}}
				options={ACKNOWLEDGEMENT_WINDOW_OPTIONS}
				errors={windowErrors.length ? Array.from(windowErrors) : undefined}
			/>
			{acknowledgementWindow === 'custom' ? (
				<Field
					labelProps={{
						children:
							'How would you describe that custom acknowledgement window?',
					}}
					inputProps={{
						id: 'acknowledgement-custom-window',
						name: 'acknowledgementCustomWindow',
						type: 'text',
						value: value.customWindow ?? '',
						onChange: (event) => onCustomWindowChange(event.target.value),
					}}
					errors={
						customWindowErrors.length
							? Array.from(customWindowErrors)
							: undefined
					}
				/>
			) : null}
			<SelectField
				labelProps={{
					children: 'Which channel do you use to follow up with complaints?',
				}}
				selectProps={{
					name: 'acknowledgementChannel',
					value: value.channel,
					onChange: (event) =>
						onChannelChange(event.target.value ?? value.channel),
				}}
				options={ACKNOWLEDGEMENT_CHANNEL_OPTIONS}
				errors={channelErrors.length ? Array.from(channelErrors) : undefined}
			/>
			<div className="space-y-2">
				<p className="text-sm font-medium">
					Do you have after-hours or weekend coverage for acknowledgements?
				</p>
				<div className="flex flex-wrap gap-4 text-sm">
					<label className="flex items-center gap-2">
						<input
							type="radio"
							name="acknowledgementCoversWeekends"
							value="true"
							checked={coversWeekends}
							onChange={() => onCoversWeekendsChange(true)}
							className="border-input accent-foreground focus-visible:ring-ring h-4 w-4 border focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden"
						/>
						<span>Yes</span>
					</label>
					<label className="flex items-center gap-2">
						<input
							type="radio"
							name="acknowledgementCoversWeekends"
							value="false"
							checked={!coversWeekends}
							onChange={() => onCoversWeekendsChange(false)}
							className="border-input accent-foreground focus-visible:ring-ring h-4 w-4 border focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden"
						/>
						<span>No</span>
					</label>
				</div>
			</div>
		</Section>
	)
}
